<template>
  <header>
    <div class="topbar">
      <div class="container-fluid">
        <div class="navbar-header">
          <div class="d-flex align-items-center gap-2">
            <!-- Menu Toggle Button -->
            <div class="topbar-item">
              <button 
                class="button-toggle-menu topbar-button" 
                style="transform: rotate(180deg);" 
                type="button" 
                @click="toggleSidebar"
              >
                <i class="ri-menu-2-line fs-24"></i>
              </button>
            </div>
            <!-- App Search-->
            <form class="app-search d-none d-md-block me-auto" @submit.prevent="handleSearch">
              <div class="position-relative">
                <input 
                  autocomplete="off" 
                  class="form-control border-0" 
                  placeholder="جستجو کنید..." 
                  type="search" 
                  v-model="searchQuery"
                />
                <i class="ri-search-line search-widget-icon"></i>
              </div>
            </form>
          </div>
          <div class="d-flex align-items-center gap-1">
            <!-- Theme Color (Light/Dark) -->
            <div class="topbar-item">
              <button 
                class="topbar-button" 
                type="button" 
                @click="toggleTheme"
              >
                <i class="ri-moon-line fs-24 light-mode" v-if="theme === 'light'"></i>
                <i class="ri-sun-line fs-24 dark-mode" v-else></i>
              </button>
            </div>
            <!-- Fullscreen -->
            <div class="dropdown topbar-item d-none d-lg-flex">
              <button 
                class="topbar-button" 
                type="button" 
                @click="toggleFullscreen"
              >
                <i class="ri-fullscreen-line fs-24 fullscreen" v-if="!isFullscreen"></i>
                <i class="ri-fullscreen-exit-line fs-24 quit-fullscreen" v-else></i>
              </button>
            </div>
            <!-- Notification -->
            <NotificationDropdown :notifications="notifications" />
            <!-- Theme Setting -->
            <div class="topbar-item d-none d-md-flex">
              <button 
                aria-controls="theme-settings-offcanvas" 
                class="topbar-button" 
                data-bs-target="#theme-settings-offcanvas" 
                data-bs-toggle="offcanvas" 
                type="button"
              >
                <i class="ri-settings-4-line fs-24"></i>
              </button>
            </div>
            <!-- User -->
            <UserDropdown />
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import { ref } from 'vue'
import { useTheme } from '@/management/composables/useTheme'
import NotificationDropdown from './NotificationDropdown.vue'
import UserDropdown from './UserDropdown.vue'
import avatar1 from '@/assets/images/users/avatar-1.jpg'
import avatar3 from '@/assets/images/users/avatar-3.jpg'
import avatar5 from '@/assets/images/users/avatar-5.jpg'

export default {
  name: 'AppTopbar',
  components: {
    NotificationDropdown,
    UserDropdown
  },
  emits: ['toggle-sidebar'],
  setup(props, { emit }) {
    const { theme, menuSize, setMenuSize, toggleTheme: toggleThemeMode } = useTheme()
    const searchQuery = ref('')
    const isFullscreen = ref(false)

    const notifications = ref([
      {
        name: 'امیرارسلان رهنما',
        message: 'روی پنل ادمین کامنت گذاشت: "وای 😍! این پنل ادمین خیلی خوبه و طراحی فوق‌العاده‌ای داره"',
        avatar: avatar1
      },
      {
        name: 'شیرین رضایی',
        message: 'سلام، حالت چطوره؟ جلسه بعدی‌مون چی شد؟',
        initials: 'د'
      },
      {
        name: 'نسترن سلطانی',
        message: 'به نظر شما روی نمودار پیش‌بینی جریان نقدی پاسخ داد 🔔.',
        avatar: avatar3
      },
      {
        name: 'سیستم',
        message: 'شما 20 پیام جدید در گفتگو دریافت کرده‌اید',
        icon: 'solar:leaf-broken',
        iconClass: 'bg-soft-warning text-warning'
      },
      {
        name: 'مهسا رهنما',
        message: 'روی ادمین نظر داد',
        avatar: avatar5
      }
    ])

    const showBackdrop = () => {
      // Check if backdrop already exists
      let backdrop = document.querySelector('.offcanvas-backdrop')
      
      if (!backdrop) {
        // Create backdrop (matching original app.js behavior)
        backdrop = document.createElement('div')
        backdrop.classList.add('offcanvas-backdrop', 'fade', 'show')
        // Ensure backdrop z-index is 1040 (below sidebar's 1055)
        backdrop.style.zIndex = '1040'
        document.body.appendChild(backdrop)
        document.body.style.overflow = 'hidden'
        
        if (window.innerWidth > 1140) {
          document.body.style.paddingRight = '15px'
        }
        
        // Use setTimeout to ensure backdrop is in DOM before adding event listener
        setTimeout(() => {
          // Close sidebar when backdrop is clicked
          backdrop.addEventListener('click', () => {
            const html = document.documentElement
            
            // Remove inline styles from sidebar
            const mainNav = document.querySelector('.main-nav')
            if (mainNav) {
              mainNav.style.zIndex = ''
              mainNav.style.position = ''
              mainNav.style.marginRight = ''
            }
            
            // Remove classes to hide sidebar
            html.classList.remove('sidebar-enable')
            document.body.classList.remove('sidebar-enable')
            
            // Remove backdrop
            if (backdrop && backdrop.parentNode) {
              document.body.removeChild(backdrop)
            }
            document.body.style.overflow = ''
            document.body.style.paddingRight = ''
          })
        }, 0)
      }
    }

    const removeBackdrop = () => {
      const backdrop = document.querySelector('.offcanvas-backdrop')
      if (backdrop && backdrop.parentNode) {
        document.body.removeChild(backdrop)
      }
      document.body.style.overflow = ''
      document.body.style.paddingRight = ''
    }

    const toggleSidebar = () => {
      // Get current menu size from HTML attribute (matching original app.js behavior)
      const html = document.documentElement
      let currentSize = html.getAttribute('data-menu-size') || menuSize.value
      const configSize = menuSize.value
      
      // Check if sidebar is currently enabled (BEFORE toggle)
      const isSidebarEnabled = html.classList.contains('sidebar-enable')
      
      // Check if we're on mobile/tablet (width <= 1199)
      const isMobile = window.innerWidth <= 1199
      
      // Ensure data-menu-size is set to 'hidden' on mobile (required for CSS to work)
      if (isMobile && currentSize !== 'hidden') {
        html.setAttribute('data-menu-size', 'hidden')
        currentSize = 'hidden'
      }
      
      // Handle menu size changes for desktop
      if (currentSize !== 'hidden' && !isMobile) {
        if (currentSize === 'condensed') {
          // If condensed, toggle back to default (or config size if config is condensed)
          const newSize = configSize === 'condensed' ? 'default' : configSize
          // Don't save - this is a temporary toggle
          setMenuSize(newSize, false)
        } else {
          // If default (or other), toggle to condensed (temporary, don't save)
          setMenuSize('condensed', false)
        }
      } else if (currentSize === 'hidden' || isMobile) {
        // For hidden menu (mobile/tablet), show/hide backdrop
        // IMPORTANT: Check state BEFORE toggling
        if (!isSidebarEnabled) {
          // Sidebar is currently closed, will be opened
          // Ensure data-menu-size is 'hidden' for CSS selector to work
          html.setAttribute('data-menu-size', 'hidden')
          
          // Add class to show sidebar (CSS: html[data-menu-size="hidden"].sidebar-enable .main-nav)
          // This sets margin-right: 0 and z-index: 1055
          html.classList.add('sidebar-enable')
          document.body.classList.add('sidebar-enable')
          
          // Ensure sidebar has correct z-index and is visible
          const mainNav = document.querySelector('.main-nav')
          if (mainNav) {
            // Force reflow to apply CSS
            void mainNav.offsetHeight
            // Ensure sidebar is above backdrop (z-index: 1055)
            mainNav.style.zIndex = '1055'
            mainNav.style.position = 'fixed'
            mainNav.style.marginRight = '0'
          }
          
          // Show backdrop after sidebar is visible (backdrop z-index: 1040, sidebar: 1055)
          // Use requestAnimationFrame to ensure DOM is updated
          requestAnimationFrame(() => {
            setTimeout(() => {
              showBackdrop()
            }, 200)
          })
        } else {
          // Sidebar is currently open, will be closed
          // Remove backdrop first
          removeBackdrop()
          
          // Remove inline styles from sidebar to allow CSS to handle it
          const mainNav = document.querySelector('.main-nav')
          if (mainNav) {
            mainNav.style.zIndex = ''
            mainNav.style.position = ''
            mainNav.style.marginRight = ''
          }
          
          // Remove class to hide sidebar (CSS will move it off-screen)
          html.classList.remove('sidebar-enable')
          document.body.classList.remove('sidebar-enable')
        }
        // Emit after handling mobile case
        emit('toggle-sidebar')
        return // Exit early for mobile
      }
      
      // For desktop: Always toggle sidebar-enable class on HTML element
      html.classList.toggle('sidebar-enable')
      document.body.classList.toggle('sidebar-enable')
      
      // Emit event
      emit('toggle-sidebar')
    }

    const toggleTheme = () => {
      toggleThemeMode()
    }

    const toggleFullscreen = () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().then(() => {
          isFullscreen.value = true
        })
      } else {
        document.exitFullscreen().then(() => {
          isFullscreen.value = false
        })
      }
    }

    const handleSearch = () => {
      // Handle search logic
      console.log('Search:', searchQuery.value)
    }

    return {
      searchQuery,
      theme,
      isFullscreen,
      notifications,
      toggleSidebar,
      toggleTheme,
      toggleFullscreen,
      handleSearch
    }
  }
}
</script>

<style scoped>
/* Component-specific styles if needed */
</style>
